import type { BlockedUserItem, BlockedUsersListParams } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { createPaginationParams, createTableQueryOptions } from '@/components/table';
import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';

const BLOCKED_USERS_BASE = 'blocked-users';

// Atom for blocked users list parameters
export const blockedUsersListParamsAtom = atom<BlockedUsersListParams>({
  page: 1,
  per_page: createPaginationParams().per_page,
  filters: {}
});

// Hydration flag to delay initial query until URL/filters are synced
export const blockedUsersHydratedAtom = atom<boolean>(false);

// Main blocked users query atom
export const blockedUsersQueryAtom = atomWithQuery((get) => {
  const params = get(blockedUsersListParamsAtom);
  const hydrated = get(blockedUsersHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['blockedUsers', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page,
          search: params.filters?.search || undefined
        },
        { domain_id: params.filters?.domain_id }
      );

      const { data } = await apiClient.get<{
        success: boolean;
        message: string;
        data: BlockedUserItem[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      }>(BLOCKED_USERS_BASE, { params: apiParams });

      return data;
    },
    enabled: hydrated
  });
});

export async function unblockBlockedUser(id: number): Promise<void> {
  await apiClient.patch(`${BLOCKED_USERS_BASE}/${id}/unblock`);
}
